import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, Star, X, Upload, FileText, Users, Loader2, ExternalLink } from "lucide-react";
import { ImageUploadField } from "@/components/ImageUploadField";
import { toast } from "sonner";

const blank = {
  title: "", slug: "", description: "", cover_image: "", pdf_upload_id: "",
  category: "Cheatsheet", price_cents: 0, free_until: "",
  published: true, is_featured_magnet: false, order_index: 100,
};

export default function AdminLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | item
  const [showLeads, setShowLeads] = useState(null); // resource id or null
  const [leads, setLeads] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/library");
      setItems(data || []);
    } catch (e) {
      toast.error("Failed to load library");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/admin/library/${id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Failed"); }
  };

  const openLeads = async (id) => {
    setShowLeads(id);
    try {
      const { data } = await api.get(`/admin/library/${id}/leads`);
      setLeads(data || []);
    } catch { toast.error("Failed to load leads"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-library-page">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">PDF Library</p>
          <h1 className="font-display text-3xl text-white uppercase tracking-tight">Free Magnets + Paid Resources</h1>
          <p className="text-sm text-zinc-400 mt-1">Upload cheatsheets, ebooks, and guides. Mark one as the <span className="text-amber-300 font-bold">featured lead magnet</span> to power the popup.</p>
        </div>
        <Button onClick={() => setEditing(blank)} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs" data-testid="library-new-btn">
          <Plus className="h-4 w-4 mr-2" /> New Resource
        </Button>
      </header>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#27272A] rounded-sm p-12 text-center text-zinc-500">
          No resources yet — create your first one.
        </div>
      ) : (
        <div className="border border-[#27272A] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F] border-b border-[#27272A] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="p-3 text-left">Title</th>
                <th className="p-3 text-left">Price</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Featured</th>
                <th className="p-3 text-left">Leads</th>
                <th className="p-3 text-left">Sales</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#27272A] hover:bg-[#0F0F0F]" data-testid={`library-row-${it.slug}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {it.cover_image && <img src={it.cover_image} alt="" className="h-10 w-10 object-cover border border-[#27272A]" />}
                      <div>
                        <div className="text-white font-bold">{it.title}</div>
                        <div className="text-[10px] text-zinc-600 font-mono">/{it.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-zinc-300">{it.price_cents > 0 ? `$${(it.price_cents / 100).toFixed(2)}` : <span className="text-emerald-400">Free</span>}</td>
                  <td className="p-3">
                    {it.published ? <span className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold">Published</span> : <span className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold">Draft</span>}
                  </td>
                  <td className="p-3">
                    {it.is_featured_magnet ? <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] uppercase tracking-widest font-bold"><Star className="h-3 w-3" />Magnet</span> : <span className="text-zinc-700 text-xs">—</span>}
                  </td>
                  <td className="p-3 text-zinc-400">{it.lead_count || 0}</td>
                  <td className="p-3 text-zinc-400">{(it.entitlement_count || 0) - (it.lead_count || 0) >= 0 ? it.entitlement_count : 0}</td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => openLeads(it.id)} className="text-zinc-400 hover:text-white h-8 px-2" data-testid={`library-leads-${it.slug}`}>
                        <Users className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(it)} className="text-zinc-400 hover:text-white h-8 px-2" data-testid={`library-edit-${it.slug}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(it.id, it.title)} className="text-zinc-400 hover:text-red-400 h-8 px-2" data-testid={`library-delete-${it.slug}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      {showLeads && (
        <Dialog open={!!showLeads} onOpenChange={(v) => !v && setShowLeads(null)}>
          <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-2xl">
            <DialogHeader><DialogTitle className="text-white uppercase tracking-tight">Leads</DialogTitle></DialogHeader>
            {leads.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">No leads yet for this resource.</div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#27272A]"><tr><th className="p-2 text-left">Name</th><th className="p-2 text-left">Email</th><th className="p-2 text-left">Phone</th><th className="p-2 text-left">Goal</th><th className="p-2 text-left">When</th></tr></thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-b border-[#1A1A1A]">
                        <td className="p-2 text-zinc-200">{l.full_name}</td>
                        <td className="p-2 text-zinc-400">{l.email}</td>
                        <td className="p-2 text-zinc-500">{l.phone || "—"}</td>
                        <td className="p-2 text-zinc-500 max-w-[200px] truncate">{l.goal || "—"}</td>
                        <td className="p-2 text-zinc-600 text-[11px]">{new Date(l.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditDialog({ item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({ ...item, price_cents: item.price_cents ?? 0 });
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfRef = useRef(null);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 80);

  const setTitle = (t) => {
    setForm((f) => ({ ...f, title: t, slug: isNew && !f.slug ? slugify(t) : f.slug }));
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Must be a PDF"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF too large (15 MB max)"); return; }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/pdf-uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, pdf_upload_id: data.id }));
      toast.success(`PDF uploaded (${Math.round(data.size / 1024)} KB)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "PDF upload failed");
    } finally { setUploadingPdf(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.pdf_upload_id) { toast.error("Please upload the PDF file"); return; }
    if (!form.cover_image) { toast.error("Please add a cover image"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, slug: form.slug || null, description: form.description,
        cover_image: form.cover_image, pdf_upload_id: form.pdf_upload_id,
        category: form.category, price_cents: Number(form.price_cents) || 0,
        free_until: form.free_until || null, published: !!form.published,
        is_featured_magnet: !!form.is_featured_magnet, order_index: Number(form.order_index) || 100,
      };
      if (isNew) {
        await api.post("/admin/library", payload);
        toast.success("Created");
      } else {
        await api.patch(`/admin/library/${item.id}`, payload);
        toast.success("Saved");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white uppercase tracking-tight">{isNew ? "New resource" : `Edit · ${item.title}`}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Title</Label>
            <Input value={form.title} onChange={(e) => setTitle(e.target.value)} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="library-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Cheatsheet · E-book · Drill Library" className="bg-[#050505] border-[#27272A] text-white rounded-sm" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Description</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="library-form-description" />
          </div>

          <ImageUploadField value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} label="Cover image" testid="library-form-cover" />

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center justify-between">
              <span>PDF file</span>
              {form.pdf_upload_id && (
                <a href={`${apiBase}/api/uploads/${form.pdf_upload_id}`} target="_blank" rel="noopener noreferrer" className="text-[#FF331F] hover:underline inline-flex items-center gap-1 normal-case tracking-normal">
                  <ExternalLink className="h-3 w-3" /> Preview
                </a>
              )}
            </Label>
            <div className="mt-2 border-2 border-dashed border-[#27272A] rounded-sm p-4 bg-[#050505]">
              <input ref={pdfRef} type="file" accept="application/pdf" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPdf(f); }} data-testid="library-form-pdf-input" />
              <div className="flex items-center gap-3">
                <Button type="button" onClick={() => pdfRef.current?.click()} disabled={uploadingPdf} className="bg-[#FF331F] hover:bg-[#FF5242] text-white text-xs uppercase tracking-widest font-bold rounded-sm" data-testid="library-form-pdf-upload">
                  {uploadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> {form.pdf_upload_id ? "Replace PDF" : "Upload PDF"}</>}
                </Button>
                {form.pdf_upload_id && (
                  <div className="text-xs text-zinc-400 inline-flex items-center gap-2">
                    <FileText className="h-4 w-4 text-emerald-400" /> PDF attached
                  </div>
                )}
              </div>
              <p className="text-[10px] text-zinc-600 mt-2">Max 15 MB · stored privately, only released via download link.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Price (cents)</Label>
              <Input type="number" min="0" value={form.price_cents} onChange={(e) => setForm({ ...form, price_cents: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="library-form-price" />
              <p className="text-[10px] text-zinc-600 mt-1">{Number(form.price_cents) > 0 ? `$${(Number(form.price_cents) / 100).toFixed(2)}` : "Free (lead capture)"}</p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Order index</Label>
              <Input type="number" value={form.order_index} onChange={(e) => setForm({ ...form, order_index: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm" />
            </div>
          </div>

          <div className="flex items-center justify-between border border-[#27272A] bg-[#050505] p-3 rounded-sm">
            <div>
              <Label className="text-white text-sm">Published</Label>
              <p className="text-[10px] text-zinc-500 mt-0.5">Visible in /library</p>
            </div>
            <Switch checked={!!form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="library-form-published" />
          </div>

          <div className="flex items-center justify-between border border-amber-700/40 bg-amber-900/10 p-3 rounded-sm">
            <div>
              <Label className="text-amber-200 text-sm inline-flex items-center gap-2"><Star className="h-3 w-3" /> Featured lead magnet</Label>
              <p className="text-[10px] text-amber-100/70 mt-0.5">Powers the popup. Only one resource can be featured at a time.</p>
            </div>
            <Switch checked={!!form.is_featured_magnet} onCheckedChange={(v) => setForm({ ...form, is_featured_magnet: v })} data-testid="library-form-featured" />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-[#27272A]">
            <Button variant="outline" onClick={onClose} className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white rounded-sm">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs rounded-sm" data-testid="library-form-save">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
